/**
 * 푸시 알림 — 앱(안드로이드)과 웹을 한자리에서(DEV-12 3).
 *
 * - 웹: 브라우저 Notification 권한만(보낼 길은 아직 없음, 기존 그대로)
 * - 앱: @capacitor/push-notifications 로 권한을 묻고 FCM 기기 토큰을 서버(POST /api/me/push-token)에 둠.
 *   알림을 누르면 data.path(서버 고정 문안의 경로)로. 로그아웃 때 이 기기 토큰을 지움
 * 알림 물음은 별채 다음 날 아침 카드 아래 한 번(시안 05 결정 19) — 그 자리(RoomPage)가 canAskPush · requestPush 를 씁니다.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '../api/client.js';

/** 앱(안드로이드 껍데기) 안인지 */
export function isNativeApp() {
    return Capacitor.isNativePlatform();
}

// 앱의 기기 권한 상태(시작할 때 한 번 읽어 둠: 화면이 그리면서 바로 물어볼 수 있게)
let nativePermission = 'unknown';
let deviceToken = null;
let started = false;

/** 아직 묻지 않았을 때만 true(웹은 Notification.permission, 앱은 기기 권한) */
export function canAskPush(win = typeof window === 'undefined' ? null : window) {
    if (isNativeApp()) {
        return nativePermission === 'prompt' || nativePermission === 'prompt-with-rationale';
    }
    return Boolean(win && 'Notification' in win && win.Notification.permission === 'default');
}

/** [알림 켜기]: 권한을 묻고, 앱이면 받자마자 기기 등록 */
export async function requestPush() {
    if (!isNativeApp()) {
        await window.Notification.requestPermission();
        return;
    }
    const { receive } = await PushNotifications.requestPermissions();
    nativePermission = receive;
    if (receive === 'granted') {
        await PushNotifications.register();
    }
}

/**
 * 앱 시작 때 한 번(로그인한 단골). 권한이 이미 있으면 기기를 다시 등록해 토큰을 서버와 맞춥니다.
 * @param {(path: string) => void} navigate  알림을 눌렀을 때 화면 이동
 */
export async function startNativePush(navigate) {
    if (!isNativeApp() || started) {
        return;
    }
    started = true;
    await PushNotifications.addListener('registration', ({ value }) => {
        deviceToken = value;
        api('/me/push-token', { method: 'POST', body: { token: value, platform: Capacitor.getPlatform() } }).catch(
            (error) => console.error('[push] 기기 토큰을 두지 못함', error?.code ?? error?.name)
        );
    });
    await PushNotifications.addListener('registrationError', (error) => {
        // google-services.json 이 없는 빌드(푸시 꺼짐)도 여기로 옵니다
        console.error('[push] 기기 등록 실패', error?.error ?? 'unknown');
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        const path = notification?.data?.path;
        // 서버 고정 문안의 앱 안 경로만(다른 주소로 끌려가지 않게)
        if (typeof path === 'string' && /^\/[a-z/]*$/u.test(path)) {
            navigate(path);
        }
    });
    const { receive } = await PushNotifications.checkPermissions();
    nativePermission = receive;
    if (receive === 'granted') {
        await PushNotifications.register();
    }
}

/** 로그아웃 직전: 이 기기 토큰을 서버에서 지움(못 지워도 로그아웃은 됨) */
export async function forgetDeviceToken() {
    if (!isNativeApp() || !deviceToken) {
        return;
    }
    const token = deviceToken;
    deviceToken = null;
    await api('/me/push-token', { method: 'DELETE', body: { token } }).catch(() => {
        // 서버가 못 받아도 다음 로그인 단골에게 토큰 주인이 바뀌어 넘어감
    });
}
